import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import PageTitle from '../../Component/Comman/PageTitle';
import { fetchKYC, submitKYC, updateKYC, fetchKYCDetails } from '../../Redux/Actions/dataActions';

function Identification({ kyc, kycDetails, fetchKYC, submitKYC, updateKYC, fetchKYCDetails, loading }){
    const hasFetched = useRef(false);
    const hasFetchedDetails = useRef(false);
    const [formData, setFormData] = useState({
        documentType: 'passport',
        documentNumber: '',
        fullName: '',
        dateOfBirth: '',
        address: '',
        country: '',
        state: '',
        city: '',
        pincode: ''
    });
    const [documentFront, setDocumentFront] = useState(null);
    const [documentBack, setDocumentBack] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState(null);
    const [submitSuccess, setSubmitSuccess] = useState(false);
    
    useEffect(() => {
        // Only fetch once when component mounts
        // kyc can be null (404 - not submitted) or an object (KYC exists)
        // We only want to fetch if we haven't fetched yet
        if (!hasFetched.current && !loading) {
            hasFetched.current = true;
            fetchKYC();
        }
    }, [fetchKYC, loading]);

    // Fetch KYC details when KYC exists (not null and not loading)
    useEffect(() => {
        if (kyc && kyc.status && !hasFetchedDetails.current && !loading) {
            hasFetchedDetails.current = true;
            fetchKYCDetails();
        }
    }, [kyc, fetchKYCDetails, loading]);

    // Pre-fill form if KYC details exist
    useEffect(() => {
        if (kycDetails && kycDetails.kyc) {
            const kycData = kycDetails.kyc;
            // Format dateOfBirth from ISO string to YYYY-MM-DD format
            let dateOfBirth = '';
            if (kycData.dateOfBirth) {
                const date = new Date(kycData.dateOfBirth);
                dateOfBirth = date.toISOString().split('T')[0];
            }
            
            setFormData({
                documentType: kycData.documentType || 'passport',
                documentNumber: kycData.documentNumber || '',
                fullName: kycData.fullName || '',
                dateOfBirth: dateOfBirth,
                address: kycData.address || '',
                country: kycData.country || '',
                state: kycData.state || '',
                city: kycData.city || '',
                pincode: kycData.pincode || ''
            });
        } else if (kyc && kyc.status && kyc.status !== 'not_submitted' && kyc.details) {
            // Fallback to basic KYC data if details not loaded yet
            let dateOfBirth = '';
            if (kyc.details.dateOfBirth) {
                const date = new Date(kyc.details.dateOfBirth);
                dateOfBirth = date.toISOString().split('T')[0];
            }
            
            setFormData({
                documentType: kyc.details.documentType || 'passport',
                documentNumber: kyc.details.documentNumber || '',
                fullName: kyc.details.fullName || '',
                dateOfBirth: dateOfBirth,
                address: kyc.details.address || '',
                country: kyc.details.country || '',
                state: kyc.details.state || '',
                city: kyc.details.city || '',
                pincode: kyc.details.pincode || ''
            });
        }
    }, [kycDetails, kyc]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleFileChange = (e, type) => {
        const file = e.target.files[0];
        if (type === 'front') {
            setDocumentFront(file);
        } else {
            setDocumentBack(file);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        setSubmitError(null);
        setSubmitSuccess(false);

        // Validate required fields
        if (!documentFront || !documentBack) {
            setSubmitError('Please upload both front and back documents');
            setSubmitting(false);
            return;
        }

        if (!formData.documentNumber || !formData.fullName || !formData.dateOfBirth || !formData.address || !formData.country || !formData.state || !formData.city || !formData.pincode) {
            setSubmitError('Please fill in all required fields');
            setSubmitting(false);
            return;
        }

        try {
            const formDataToSend = new FormData();
            formDataToSend.append('documentFront', documentFront);
            formDataToSend.append('documentBack', documentBack);
            formDataToSend.append('documentType', formData.documentType);
            formDataToSend.append('documentNumber', formData.documentNumber);
            formDataToSend.append('fullName', formData.fullName);
            formDataToSend.append('dateOfBirth', formData.dateOfBirth);
            formDataToSend.append('address', formData.address);
            formDataToSend.append('country', formData.country);
            formDataToSend.append('state', formData.state);
            formDataToSend.append('city', formData.city);
            formDataToSend.append('pincode', formData.pincode);

            let result;
            if (isNotSubmitted) {
                result = await submitKYC(formDataToSend);
            } else {
                result = await updateKYC(formDataToSend);
            }

            if (result.success) {
                setSubmitSuccess(true);
                // Refresh KYC data and details
                fetchKYC();
                // Reset fetch flags to allow refetching details
                hasFetchedDetails.current = false;
                setTimeout(() => {
                    fetchKYCDetails();
                }, 500);
                // Reset form after 2 seconds
                setTimeout(() => {
                    setSubmitSuccess(false);
                }, 2000);
            } else {
                setSubmitError(result.error || 'Failed to submit KYC');
            }
        } catch (error) {
            setSubmitError(error.message || 'An error occurred');
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <>
                <div className="body-header border-bottom d-flex py-3 mb-3">
                    <PageTitle pagetitle='Personal Verification' sidebutton={true}/>
                </div>
                <div className="container-xxl">
                    <div className="row mb-3">
                        <div className="col-xl-12">
                            <div className="card">
                                <div className="card-body">
                                    <div className="text-center py-4">
                                        <div className="spinner-border text-primary" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    // If kyc is null, it means KYC hasn't been submitted yet (404 response)
    // KYC structure: { status, details: { fullName, dateOfBirth, address, documentType, documentNumber }, ... }
    const kycStatus = kyc?.status || 'not_submitted';
    const isVerified = kycStatus === 'verified' || kycStatus === 'approved';
    const isPending = kycStatus === 'pending' || kycStatus === 'under_review';
    const isRejected = kycStatus === 'rejected' || kycStatus === 'failed';
    const isNotSubmitted = kycStatus === 'not_submitted' || !kyc;

    const limits = kyc?.limits || {};
    const features = kyc?.features || {};

    const getStatusConfig = (status) => {
        const statusLower = (status || '').toLowerCase();
        
        if (statusLower === 'verified' || statusLower === 'approved') {
            return {
                badgeClass: 'bg-success',
                icon: 'icofont-check-circled',
                text: 'Verified',
                description: 'Your KYC has been verified successfully'
            };
        } else if (statusLower === 'pending' || statusLower === 'under_review') {
            return {
                badgeClass: 'bg-warning',
                icon: 'icofont-clock-time',
                text: 'Under Review',
                description: 'Your documents are being reviewed by our team'
            };
        } else if (statusLower === 'rejected' || statusLower === 'failed') {
            return {
                badgeClass: 'bg-danger',
                icon: 'icofont-close-circled',
                text: 'Rejected',
                description: 'Your KYC submission was rejected. Please update your documents'
            };
        } else {
            return {
                badgeClass: 'bg-secondary',
                icon: 'icofont-info-circle',
                text: 'Not Submitted',
                description: 'Please submit your KYC documents to complete verification'
            };
        }
    };

    const statusConfig = getStatusConfig(kycStatus);

    return(<>
        <div className="body-header border-bottom d-flex py-3 mb-3">
            <PageTitle pagetitle='Personal Verification' sidebutton={true}/>
        </div>
        <div className="container-xxl">
                <div className="row mb-3">
                    <div className="col-xl-12">
                        <div className="card">
                        <div className="card-header py-3 d-flex justify-content-between bg-transparent align-items-center ">
                            <h6 className="mb-0 fw-bold">Verification</h6>
                        </div>
                            <div className="card-body">
                                <div className="row g-3">
                                <div className="col-xl-12">
                                    <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
                                        <div className="d-flex align-items-center gap-3">
                                            <div className={`badge ${statusConfig.badgeClass} rounded-pill px-3 py-2 d-flex align-items-center gap-2`} style={{ fontSize: '0.9rem' }}>
                                                <i className={`${statusConfig.icon} fs-5`}></i>
                                                <span className="fw-bold">{statusConfig.text}</span>
                                            </div>
                                            <div className="d-flex flex-column">
                                                <h5 className="mb-0 fw-bold">Identity Verification Status</h5>
                                                <small className="text-muted">{statusConfig.description}</small>
                                            </div>
                                        </div>
                                        {kycDetails && kycDetails.kyc && kycDetails.kyc.submittedAt && (
                                            <div className="text-end">
                                                <small className="text-muted d-block">Submitted</small>
                                                <small className="fw-semibold">
                                                    {new Date(kycDetails.kyc.submittedAt).toLocaleDateString('en-US', { 
                                                        year: 'numeric', 
                                                        month: 'short', 
                                                        day: 'numeric' 
                                                    })}
                                                </small>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                {/* Display KYC Details when available */}
                                {kycDetails && kycDetails.kyc && (
                                    <>
                                        <div className="col-xl-12">
                                            <h6 className="mb-3">KYC Details</h6>
                                            <div className="row g-3">
                                                <div className="col-md-6">
                                                    <div className="d-flex flex-column">
                                                        <span className="text-muted small">Full Name</span>
                                                        <span className="fw-bold">{kycDetails.kyc.fullName || 'N/A'}</span>
                                                    </div>
                                                </div>
                                                <div className="col-md-6">
                                                    <div className="d-flex flex-column">
                                                        <span className="text-muted small">Date of Birth</span>
                                                        <span className="fw-bold">
                                                            {kycDetails.kyc.dateOfBirth 
                                                                ? new Date(kycDetails.kyc.dateOfBirth).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
                                                                : 'N/A'}
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="col-md-6">
                                                    <div className="d-flex flex-column">
                                                        <span className="text-muted small">Document Type</span>
                                                        <span className="fw-bold text-capitalize">{kycDetails.kyc.documentType || 'N/A'}</span>
                                                    </div>
                                                </div>
                                                <div className="col-md-6">
                                                    <div className="d-flex flex-column">
                                                        <span className="text-muted small">Document Number</span>
                                                        <span className="fw-bold">{kycDetails.kyc.documentNumber || 'N/A'}</span>
                                                    </div>
                                                </div>
                                                <div className="col-md-12">
                                                    <div className="d-flex flex-column">
                                                        <span className="text-muted small">Address</span>
                                                        <span className="fw-bold">{kycDetails.kyc.address || 'N/A'}</span>
                                                    </div>
                                                </div>
                                                <div className="col-md-6">
                                                    <div className="d-flex flex-column">
                                                        <span className="text-muted small">Country</span>
                                                        <span className="fw-bold">{kycDetails.kyc.country || 'N/A'}</span>
                                                    </div>
                                                </div>
                                                <div className="col-md-6">
                                                    <div className="d-flex flex-column">
                                                        <span className="text-muted small">State</span>
                                                        <span className="fw-bold">{kycDetails.kyc.state || 'N/A'}</span>
                                                    </div>
                                                </div>
                                                <div className="col-md-6">
                                                    <div className="d-flex flex-column">
                                                        <span className="text-muted small">City</span>
                                                        <span className="fw-bold">{kycDetails.kyc.city || 'N/A'}</span>
                                                    </div>
                                                </div>
                                                <div className="col-md-6">
                                                    <div className="d-flex flex-column">
                                                        <span className="text-muted small">Pincode</span>
                                                        <span className="fw-bold">{kycDetails.kyc.pincode || 'N/A'}</span>
                                                    </div>
                                                </div>
                                                {kycDetails.kyc.documentFront && (
                                                    <div className="col-md-6">
                                                        <div className="d-flex flex-column">
                                                            <span className="text-muted small mb-2">Document Front</span>
                                                            <img 
                                                                src={kycDetails.kyc.documentFront} 
                                                                alt="Document Front" 
                                                                className="img-fluid border rounded"
                                                                style={{ maxHeight: '300px', objectFit: 'contain' }}
                                                            />
                                                        </div>
                                                    </div>
                                                )}
                                                {kycDetails.kyc.documentBack && (
                                                    <div className="col-md-6">
                                                        <div className="d-flex flex-column">
                                                            <span className="text-muted small mb-2">Document Back</span>
                                                            <img 
                                                                src={kycDetails.kyc.documentBack} 
                                                                alt="Document Back" 
                                                                className="img-fluid border rounded"
                                                                style={{ maxHeight: '300px', objectFit: 'contain' }}
                                                            />
                                                        </div>
                                                    </div>
                                                )}
                                                {kycDetails.kyc.submittedAt && (
                                                    <div className="col-md-6">
                                                        <div className="d-flex flex-column">
                                                            <span className="text-muted small">Submitted At</span>
                                                            <span className="fw-bold">
                                                                {new Date(kycDetails.kyc.submittedAt).toLocaleString('en-US')}
                                                            </span>
                                                        </div>
                                                    </div>
                                                )}
                                                {kycDetails.kyc.updatedAt && (
                                                    <div className="col-md-6">
                                                        <div className="d-flex flex-column">
                                                            <span className="text-muted small">Last Updated</span>
                                                            <span className="fw-bold">
                                                                {new Date(kycDetails.kyc.updatedAt).toLocaleString('en-US')}
                                                            </span>
                                                        </div>
                                                    </div>
                                                )}
                                                {kycDetails.kyc.remarks && (
                                                    <div className="col-md-12">
                                                        <div className="d-flex flex-column">
                                                            <span className="text-muted small">Remarks</span>
                                                            <span className="fw-bold">{kycDetails.kyc.remarks}</span>
                                                        </div>
                                                    </div>
                                                )}
                                                {kycDetails.userVerification && (
                                                    <div className="col-md-12">
                                                        <div className="d-flex flex-column">
                                                            <span className="text-muted small mb-2">User Verification</span>
                                                            <div className="d-flex gap-3">
                                                                <span className={`badge ${kycDetails.userVerification.isEmailVerified ? 'bg-success' : 'bg-danger'}`}>
                                                                    Email: {kycDetails.userVerification.isEmailVerified ? 'Verified' : 'Not Verified'}
                                                                </span>
                                                                <span className={`badge ${kycDetails.userVerification.isMobileVerified ? 'bg-success' : 'bg-danger'}`}>
                                                                    Mobile: {kycDetails.userVerification.isMobileVerified ? 'Verified' : 'Not Verified'}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </>
                                )}
                                {/* <div className="col-xl-9">
                                    <h6 className="mb-3">Current Features</h6>
                                    <div className="row g-3 row-cols-1 row-cols-sm-1 row-cols-md-2 row-cols-lg-2 row-cols-xl-2 row-cols-xxl-3">
                                        <div className="col">
                                            <div className="d-flex flex-column">
                                                <div className="d-flex flex-column">
                                                    <span className="text-muted">Fiat Deposit &amp; Withdrawal Limits</span>
                                                    <span className="fw-bold">{limits.fiatDepositLimit || limits.fiatDailyLimit || '$50K Daily'}</span>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col">
                                            <div className="d-flex flex-column">
                                                <div className="d-flex flex-column">
                                                    <span className="text-muted">Crypto Deposit Limit</span>
                                                    <span className="fw-bold">{limits.cryptoDepositLimit || 'Unlimited'}</span>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col">
                                            <div className="d-flex flex-column">
                                                <div className="d-flex flex-column">
                                                    <span className="text-muted">Crypto Withdrawal Limit</span>
                                                    <span className="fw-bold">{limits.cryptoWithdrawalLimit || limits.cryptoDailyWithdrawalLimit || '100 BTC Daily'}</span>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col">
                                            <div className="d-flex flex-column">
                                                <div className="d-flex flex-column">
                                                    <span className="text-muted">P2P Transaction Limits</span>
                                                    <span className="fw-bold">{limits.p2pLimit || 'Unlimited'}</span>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col">
                                            <div className="d-flex flex-column">
                                                <div className="d-flex flex-column">
                                                    <span className="text-muted">Other Features</span>
                                                    <span className="fw-bold">{features.otherFeatures || 'LPD/OTC/Cryptoon card'}</span>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col">
                                            <div className="d-flex flex-column">
                                                <div className="d-flex flex-column">
                                                    <span className="text-muted">Ico Apply</span>
                                                    <span className="fw-bold">{features.icoApply || 'Unlimited'}</span>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col">
                                            <div className="d-flex flex-column">
                                                <div className="d-flex flex-column">
                                                    <span className="text-muted">Loan Apply</span>
                                                    <span className="fw-bold">{features.loanApply || 'Unlimited'}</span>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col">
                                            <div className="d-flex flex-column">
                                                <div className="d-flex flex-column">
                                                    <span className="text-muted">Pay Transaction</span>
                                                    <span className="fw-bold">{limits.payTransactionLimit || limits.payDailyLimit || '$20K Daily'}</span>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col">
                                            <div className="d-flex flex-column">
                                                <div className="d-flex flex-column">
                                                    <span className="text-muted">Future Trade</span>
                                                    <span className="fw-bold">{features.futureTrade || 'Unlimited'}</span>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col">
                                            <div className="d-flex flex-column">
                                                <div className="d-flex flex-column">
                                                    <span className="text-muted">Spot Stand</span>
                                                    <span className="fw-bold">{features.spotStand || 'Unlimited Day'}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div> */}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                {/* KYC Submission Form - Show when not submitted or rejected */}
                {(isNotSubmitted || isRejected) && (
                    <div className="row mb-3">
                        <div className="col-xl-12">
                            <div className="card">
                                <div className="card-header py-3 d-flex justify-content-between bg-transparent align-items-center">
                                    <h6 className="mb-0 fw-bold">{isNotSubmitted ? 'Submit KYC Documents' : 'Update KYC Documents'}</h6>
                                </div>
                                <div className="card-body">
                                    {submitSuccess && (
                                        <div className="alert alert-success alert-dismissible fade show" role="alert">
                                            KYC documents submitted successfully!
                                            <button type="button" className="btn-close" onClick={() => setSubmitSuccess(false)}></button>
                                        </div>
                                    )}
                                    {submitError && (
                                        <div className="alert alert-danger alert-dismissible fade show" role="alert">
                                            {submitError}
                                            <button type="button" className="btn-close" onClick={() => setSubmitError(null)}></button>
                                        </div>
                                    )}
                                    <form onSubmit={handleSubmit}>
                                        <div className="row g-3">
                                            <div className="col-md-6">
                                                <label htmlFor="documentType" className="form-label">Document Type <span className="text-danger">*</span></label>
                                                <select 
                                                    className="form-select" 
                                                    id="documentType" 
                                                    name="documentType"
                                                    value={formData.documentType}
                                                    onChange={handleInputChange}
                                                    required
                                                >
                                                    <option value="passport">Passport</option>
                                                    <option value="national_id">National ID</option>
                                                    <option value="driving_license">Driving License</option>
                                                </select>
                                            </div>
                                            <div className="col-md-6">
                                                <label htmlFor="documentNumber" className="form-label">Document Number <span className="text-danger">*</span></label>
                                                <input 
                                                    type="text" 
                                                    className="form-control" 
                                                    id="documentNumber" 
                                                    name="documentNumber"
                                                    value={formData.documentNumber}
                                                    onChange={handleInputChange}
                                                    required
                                                />
                                            </div>
                                            <div className="col-md-6">
                                                <label htmlFor="fullName" className="form-label">Full Name <span className="text-danger">*</span></label>
                                                <input 
                                                    type="text" 
                                                    className="form-control" 
                                                    id="fullName" 
                                                    name="fullName"
                                                    value={formData.fullName}
                                                    onChange={handleInputChange}
                                                    required
                                                />
                                            </div>
                                            <div className="col-md-6">
                                                <label htmlFor="dateOfBirth" className="form-label">Date of Birth <span className="text-danger">*</span></label>
                                                <input 
                                                    type="date" 
                                                    className="form-control" 
                                                    id="dateOfBirth" 
                                                    name="dateOfBirth"
                                                    value={formData.dateOfBirth}
                                                    onChange={handleInputChange}
                                                    required
                                                />
                                            </div>
                                            <div className="col-md-12">
                                                <label htmlFor="address" className="form-label">Address <span className="text-danger">*</span></label>
                                                <textarea 
                                                    className="form-control" 
                                                    id="address" 
                                                    name="address"
                                                    rows="3"
                                                    value={formData.address}
                                                    onChange={handleInputChange}
                                                    required
                                                />
                                            </div>
                                            <div className="col-md-6">
                                                <label htmlFor="country" className="form-label">Country <span className="text-danger">*</span></label>
                                                <input 
                                                    type="text" 
                                                    className="form-control" 
                                                    id="country" 
                                                    name="country"
                                                    value={formData.country}
                                                    onChange={handleInputChange}
                                                    required
                                                />
                                            </div>
                                            <div className="col-md-6">
                                                <label htmlFor="state" className="form-label">State <span className="text-danger">*</span></label>
                                                <input 
                                                    type="text" 
                                                    className="form-control" 
                                                    id="state" 
                                                    name="state"
                                                    value={formData.state}
                                                    onChange={handleInputChange}
                                                    required
                                                />
                                            </div>
                                            <div className="col-md-6">
                                                <label htmlFor="city" className="form-label">City <span className="text-danger">*</span></label>
                                                <input 
                                                    type="text" 
                                                    className="form-control" 
                                                    id="city" 
                                                    name="city"
                                                    value={formData.city}
                                                    onChange={handleInputChange}
                                                    required
                                                />
                                            </div>
                                            <div className="col-md-6">
                                                <label htmlFor="pincode" className="form-label">Pincode <span className="text-danger">*</span></label>
                                                <input 
                                                    type="text" 
                                                    className="form-control" 
                                                    id="pincode" 
                                                    name="pincode"
                                                    value={formData.pincode}
                                                    onChange={handleInputChange}
                                                    required
                                                    maxLength="6"
                                                    pattern="[0-9]{6}"
                                                    title="Pincode must be 6 digits"
                                                />
                                            </div>
                                            <div className="col-md-6">
                                                <label htmlFor="documentFront" className="form-label">Document Front (Photo) <span className="text-danger">*</span></label>
                                                <input 
                                                    type="file" 
                                                    className="form-control" 
                                                    id="documentFront" 
                                                    accept="image/*,.pdf"
                                                    onChange={(e) => handleFileChange(e, 'front')}
                                                    required={!documentFront}
                                                />
                                                {documentFront && (
                                                    <small className="text-muted d-block mt-1">Selected: {documentFront.name}</small>
                                                )}
                                            </div>
                                            <div className="col-md-6">
                                                <label htmlFor="documentBack" className="form-label">Document Back (Photo) <span className="text-danger">*</span></label>
                                                <input 
                                                    type="file" 
                                                    className="form-control" 
                                                    id="documentBack" 
                                                    accept="image/*,.pdf"
                                                    onChange={(e) => handleFileChange(e, 'back')}
                                                    required={!documentBack}
                                                />
                                                {documentBack && (
                                                    <small className="text-muted d-block mt-1">Selected: {documentBack.name}</small>
                                                )}
                                            </div>
                                            <div className="col-12">
                                                <button 
                                                    type="submit" 
                                                    className="btn btn-primary"
                                                    disabled={submitting}
                                                >
                                                    {submitting ? (
                                                        <>
                                                            <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                                            Submitting...
                                                        </>
                                                    ) : (
                                                        isNotSubmitted ? 'Submit KYC' : 'Update KYC'
                                                    )}
                                                </button>
                                            </div>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
        </div>
    </>
    )
}

const mapStateToProps = (state) => ({
    kyc: state.data?.kyc,
    kycDetails: state.data?.kycDetails,
    loading: state.data?.loading?.kyc || state.data?.loading?.kycDetails || false,
});

export default connect(mapStateToProps, { fetchKYC, submitKYC, updateKYC, fetchKYCDetails })(Identification);